import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { CacheService } from '../core/services/cache/cache.service';
import { LayoutService } from '../core/services/layout/layout.service';
import { LocalStorageService } from '../core/services/local-storage/local-storage.service';
import { AIAssistantBotComponent } from '../shared/components/ai-assistant-bot/ai-assistant-bot.component';
import { DashboardFooterComponent } from '../shared/components/footer/dashboard-footer/dashboard-footer.component';
import { FooterComponent } from '../shared/components/footer/footer.component';
import { HeaderComponent } from '../shared/components/header/header.component';
import { SidebarComponent } from '../shared/components/sidebar/sidebar.component';
import { TopNavigationComponent } from '../shared/components/top-navigation/top-navigation.component';
import { WidgetMenuComponent } from '../shared/components/widget-menu/widget-menu/widget-menu.component';
import { UserGuidanceService } from '../shared/services/analytics/user-guidance.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    DashboardFooterComponent,
    SidebarComponent,
    TopNavigationComponent,
    AIAssistantBotComponent,
    WidgetMenuComponent,
  ],
  templateUrl: './layout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
  showFooter = signal(true);
  showDashboardFooter = signal(false);

  router = inject(Router);
  cacheService = inject(CacheService);
  localStorageService = inject(LocalStorageService);
  guidanceService = inject(UserGuidanceService);
  layoutService = inject(LayoutService);

  constructor() {
    // Re-check footer visibility whenever navigation mode changes.
    // Defer writes so chrome remount (header ↔ top-bar) does not trip NG0100.
    effect(() => {
      const _mode = this.layoutService.actualNavigationMode();
      const url = this.router.url;
      queueMicrotask(() => this.updateFooterForRoute(url));
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.localStorageService.getToken()) {
      try {
        await firstValueFrom(this.cacheService.getCurrentUser(false));
      } catch (error) {
        console.error('Failed to load current user in Layout:', error);
      }
    }

    // Handle initial route
    this.updateFooterForRoute(this.router.url);

    // Listen for route changes — refresh Role.permissions so revokes apply without full reload
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateFooterForRoute(event.urlAfterRedirects || event.url);
        void this.refreshCurrentUserPermissions();
      });

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.onWindowFocus);
    }
  }

  /**
   * Refreshes the current user (including Role.permissions) on window focus.
   */
  private readonly onWindowFocus = (): void => {
    void this.refreshCurrentUserPermissions();
  };

  /** Removes focus listener when the layout shell is destroyed. */
  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.onWindowFocus);
    }
  }

  /**
   * Reloads currentUser from the API so permission changes take effect in-session.
   */
  private async refreshCurrentUserPermissions(): Promise<void> {
    if (!this.localStorageService.getToken()) {
      return;
    }
    try {
      await firstValueFrom(this.cacheService.getCurrentUser(true));
    } catch (error) {
      console.error('Failed to refresh current user permissions:', error);
    }
  }

  private updateFooterForRoute(_url: string): void {
    const noFooter =
      this.layoutService.actualNavigationMode() === 'side-menu' ||
      this.layoutService.actualNavigationMode() === 'top-bar';
    this.showDashboardFooter.set(false);
    this.showFooter.set(!noFooter);
  }
}
